/**
 * Minimal dependency-free ZIP writer (STORE method, no compression).
 * Produces a standards-compliant .zip from a list of { path, content }
 * entries — enough for source-code downloads without pulling in JSZip.
 */

export interface ZipEntry {
  path: string;
  content: string;
}

/* CRC-32 (IEEE 802.3) lookup table */
const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

export function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function writeUint16(view: DataView, offset: number, value: number) {
  view.setUint16(offset, value & 0xffff, true);
}

function writeUint32(view: DataView, offset: number, value: number) {
  view.setUint32(offset, value >>> 0, true);
}

/** Build a .zip Blob from entries. Paths use forward slashes. */
export function buildZip(entries: ZipEntry[]): Blob {
  const encoder = new TextEncoder();
  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  let offset = 0;

  for (const entry of entries) {
    const nameBytes = encoder.encode(entry.path.replace(/\\/g, '/').replace(/^\/+/, ''));
    const dataBytes = encoder.encode(entry.content);
    const crc = crc32(dataBytes);

    // Local file header (30 bytes + name) + data
    const local = new Uint8Array(30 + nameBytes.length + dataBytes.length);
    const lv = new DataView(local.buffer);
    writeUint32(lv, 0, 0x04034b50); // local header signature
    writeUint16(lv, 4, 20);         // version needed
    writeUint16(lv, 6, 0x0800);     // flags: UTF-8 names
    writeUint16(lv, 8, 0);          // method: store
    writeUint16(lv, 10, 0);         // mod time
    writeUint16(lv, 12, 0);         // mod date
    writeUint32(lv, 14, crc);
    writeUint32(lv, 18, dataBytes.length); // compressed size
    writeUint32(lv, 22, dataBytes.length); // uncompressed size
    writeUint16(lv, 26, nameBytes.length);
    writeUint16(lv, 28, 0);         // extra length
    local.set(nameBytes, 30);
    local.set(dataBytes, 30 + nameBytes.length);
    localParts.push(local);

    // Central directory record (46 bytes + name)
    const central = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(central.buffer);
    writeUint32(cv, 0, 0x02014b50); // central dir signature
    writeUint16(cv, 4, 20);         // version made by
    writeUint16(cv, 6, 20);         // version needed
    writeUint16(cv, 8, 0x0800);     // flags: UTF-8
    writeUint16(cv, 10, 0);         // method: store
    writeUint16(cv, 12, 0);         // mod time
    writeUint16(cv, 14, 0);         // mod date
    writeUint32(cv, 16, crc);
    writeUint32(cv, 20, dataBytes.length);
    writeUint32(cv, 24, dataBytes.length);
    writeUint16(cv, 28, nameBytes.length);
    writeUint32(cv, 42, offset);    // local header offset
    central.set(nameBytes, 46);
    centralParts.push(central);

    offset += local.length;
  }

  const centralSize = centralParts.reduce((s, p) => s + p.length, 0);

  // End of central directory (22 bytes)
  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  writeUint32(ev, 0, 0x06054b50);
  writeUint16(ev, 8, entries.length);
  writeUint16(ev, 10, entries.length);
  writeUint32(ev, 12, centralSize);
  writeUint32(ev, 16, offset);

  return new Blob([...localParts, ...centralParts, eocd] as BlobPart[], {
    type: 'application/zip',
  });
}

/** Trigger a browser download of a Blob. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}
